import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useDeleteGlobalConclusion } from '../../../hooks/api/correctionsV2/useDeleteGlobalConclusion';
import useGetGlobalConclusions from '../../../hooks/api/correctionsV2/useGetGlobalConclusions';
import { addLicense } from '../../../redux/globalLicenseCorrections/actions';
import { rebuildRevision } from '../../../redux/globalLicenseCorrections/rebuildRevision';
import { selectors } from '../../../redux/globalLicenseCorrections/slice';
import { BUTTON_SIZE, BUTTON_TYPE, LoaderButton } from '../../generic';
import Button, { TooltipButton } from '../../generic/Button';
import Flex from '../../generic/Flex';
import { LoadingBoundary } from '../../generic/LoadingBoundary';
import NewModalAddLicense from '../../modals/NewModalAddLicense';
import type { NewLicense } from '../../modals/NewModalAddLicense';

import css from './BottomActions.module.less';

type BottomActionsLeftProps = {
  dependencyRevisionLocator: string;
};

/**
 * Buttons for the left side of the bottom actions.
 * Contains the add-license button.
 */
const BottomActionsLeft = ({ dependencyRevisionLocator }: BottomActionsLeftProps) => {
  const dispatch = useDispatch();
  const { data: conclusions, isLoading: isLoadingConclusions } = useGetGlobalConclusions(dependencyRevisionLocator);
  const { mutateAsync: deleteGlobalConclusion, isPending: isDeletingGlobalConclusion } =
    useDeleteGlobalConclusion(dependencyRevisionLocator);
  const [isModalVisible, setModalVisible] = React.useState(false);

  const onAddLicense = (newLicense: NewLicense) => {
    dispatch(addLicense(newLicense));
    setModalVisible(false);
  };

  const onResetConclusions = async () => {
    await deleteGlobalConclusion();
  };

  const hasGlobalConclusions = conclusions && conclusions.globalConcludedLicenses.length > 0;

  return (
    <Flex gap="8px">
      <Button onClick={() => setModalVisible(true)} type={BUTTON_TYPE.PRIMARY} size={BUTTON_SIZE.LARGE}>
        Add License
      </Button>
      {hasGlobalConclusions && (
        <TooltipButton
          tooltip="Deletes all global conclusions for this revision. This will reset the conclusions license to the base."
          id="reset-conclusions-button"
          onClick={onResetConclusions}
          isDisabled={isDeletingGlobalConclusion || isLoadingConclusions}
          type={BUTTON_TYPE.ERROR}
          size={BUTTON_SIZE.LARGE}
        >
          Reset Conclusions
        </TooltipButton>
      )}
      {isModalVisible && (
        <NewModalAddLicense
          onSave={onAddLicense}
          onCancel={() => setModalVisible(false)}
          isNested={false}
          shouldSkipWarning
        />
      )}
    </Flex>
  );
};

/**
 * Buttons for the right side of the bottom actions.
 * Contains the rebuild button and task info.
 */
const BottomActionsRight = () => {
  const scheduledTaskValue = useSelector(selectors.scheduledTaskSelector);

  return (
    <LoadingBoundary
      asyncValue={scheduledTaskValue}
      renderValue={({ value: scheduledTask }) =>
        <div className={css.bottomActions}>
          <span>Rebuild scheduled: Task #{scheduledTask.id}</span>
          <RebuildButton />
        </div>}
      renderEmpty={(
        <div className={css.bottomActions}>
          <RebuildButton />
        </div>)}
      renderError={({ errors }) => <span>error: ${errors.join('. ')}</span>}
      renderLoading={(
        <div className={css.bottomActions}>
          <RebuildButton isLoading isDisabled />
        </div>)}
    />
  );
};

type RebuildButtonProps = {
  isLoading?: boolean;
  isDisabled?: boolean;
};

const RebuildButton: React.FC<RebuildButtonProps> = ({ isLoading, isDisabled }) => {
  const dispatch = useDispatch();

  return (
    <LoaderButton
      type={BUTTON_TYPE.PRIMARY}
      size={BUTTON_SIZE.LARGE}
      onClick={() => dispatch(rebuildRevision())}
      isLoading={isLoading}
      isDisabled={isDisabled}
    >
      Rebuild
    </LoaderButton>);
};

type BottomActionsProps = {
  dependencyRevisionLocator: string;
};

/** Action buttons for the bottom of the License Corrections Card. */
export const BottomActions = ({ dependencyRevisionLocator }: BottomActionsProps) =>
  <div className={css.bottomActions}>
    <BottomActionsLeft dependencyRevisionLocator={dependencyRevisionLocator} />
    <BottomActionsRight />
  </div>;
